/**
 * Dashboard Page
 *
 * หน้า Dashboard สำหรับแสดงภาพรวมข้อมูล
 * ออกแบบตามหลักการเว็บพนันออนไลน์ - เน้นกำไรสุทธิเป็นหลัก
 */

import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  Gift,
  RotateCcw,
  Users,
  UserPlus,
  UserCheck,
  Activity,
  TrendingUp,
  TrendingDown,
  RefreshCcw,
  Calendar,
  ChevronRight,
  Building2,
  Zap,
  User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import { DASHBOARD_CONFIG } from "@/constants/dashboard";
import { useAuth } from "@/components/auth/auth-provider";
import { DashboardService } from "@/domain/dashboard";
import type {
  FinancialStats,
  MemberStats,
  DateFilterType,
} from "@/domain/dashboard";

// ============================================
// Types
// ============================================

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  trend?: {
    value: string;
    isUp: boolean;
  };
  size?: "default" | "large";
  className?: string;
  valueColor?: string; // Custom color for value text
}

// ============================================
// Components
// ============================================

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  iconColor,
  iconBg,
  trend,
  size = "default",
  className,
  valueColor,
}: StatCardProps) {
  const { t } = useTranslation();
  return (
    <Card className={cn("relative overflow-hidden", className)}>
      <CardContent className={cn("p-5", size === "large" && "p-6")}>
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-muted-foreground truncate">
              {title}
            </p>
            <p
              className={cn(
                "font-bold mt-1",
                size === "large" ? "text-4xl" : "text-2xl",
                valueColor
              )}
            >
              {typeof value === "number"
                ? value.toLocaleString()
                : value}
            </p>
            {subtitle && (
              <p className="text-[11px] text-muted-foreground/60 mt-2 tracking-wide">
                {subtitle}
              </p>
            )}
          </div>
          <div className={cn("p-3 rounded-xl", iconBg)}>
            <Icon className={cn("h-6 w-6", iconColor)} />
          </div>
        </div>
        {trend && (
          <div className="flex items-center gap-1 mt-3">
            {trend.isUp ? (
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            ) : (
              <TrendingDown className="h-4 w-4 text-rose-500" />
            )}
            <span
              className={cn(
                "text-sm font-medium",
                trend.isUp ? "text-emerald-500" : "text-rose-500"
              )}
            >
              {trend.value}
            </span>
            <span className="text-xs text-muted-foreground">{t("dashboard.vsYesterday")}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatCardSkeleton({
  size = "default",
}: {
  size?: "default" | "large";
}) {
  return (
    <Card>
      <CardContent className={cn("p-5", size === "large" && "p-6")}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <Skeleton className="h-4 w-24" />
            <Skeleton
              className={cn("mt-2", size === "large" ? "h-9 w-40" : "h-8 w-32")}
            />
          </div>
          <Skeleton className="h-12 w-12 rounded-xl" />
        </div>
        <Skeleton className="h-4 w-28 mt-3" />
      </CardContent>
    </Card>
  );
}

// ============================================
// Main Component
// ============================================

export function DashboardPage() {
  const { t } = useTranslation();
  const { sessionToken } = useAuth();

  // State
  const [dateFilter, setDateFilter] = useState<DateFilterType>("today");
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [debugInfo, setDebugInfo] = useState<string>("");
  const [apiError, setApiError] = useState<string | null>(null);

  const [financialStats, setFinancialStats] = useState<FinancialStats | null>(
    null
  );
  const [memberStats, setMemberStats] = useState<MemberStats | null>(null);

  // Load data with timeout
  const loadData = async (showLoading = true) => {
    logger.debug(
      "🚀 [Dashboard] loadData called, sessionToken:",
      sessionToken ? "✅ exists" : "❌ missing"
    );
    setApiError(null);
    setDebugInfo(t("dashboard.loadingData"));

    if (!sessionToken) {
      // No session - show empty data instead of loading forever
      logger.warn("⚠️ [Dashboard] No session token, skipping data load");
      setDebugInfo(`❌ ${t("dashboard.noSessionToken")}`);
      setIsLoading(false);
      return;
    }

    if (showLoading) setIsLoading(true);
    else setIsRefreshing(true);

    try {
      const dateRange = DashboardService.getDateRange(
        dateFilter as "today" | "yesterday" | "7days" | "30days"
      );
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      setDebugInfo(
        `📅 ${t("dashboard.loadingDataFor")} ${t(`dashboard.dateFilter.${dateFilter}`)}: ${params.startDate.toLocaleDateString()} - ${params.endDate.toLocaleDateString()}`
      );

      logger.debug("📅 [Dashboard] Date range:", {
        filter: dateFilter,
        start: params.startDate.toISOString(),
        end: params.endDate.toISOString(),
      });

      // Add timeout (8 seconds - improved from 10s)
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error("Request timeout")),
          DASHBOARD_CONFIG.API_TIMEOUT_MS
        )
      );

      const [financial, members] = (await Promise.race([
        Promise.all([
          DashboardService.getFinancialStats(sessionToken, params),
          DashboardService.getMemberStats(sessionToken, params),
        ]),
        timeoutPromise.then(() => {
          throw new Error("timeout");
        }),
      ])) as [FinancialStats, MemberStats];

      logger.debug("✅ [Dashboard] Data loaded:", {
        financial: {
          netProfit: financial.netProfit,
          deposit: financial.totalDepositRegularAmount,
          withdraw: financial.totalWithdrawRegularAmount,
          transactions: financial.totalTransactions,
        },
        members: {
          total: members.totalMembers,
          new: members.totalNewMembers,
        },
      });

      setFinancialStats(financial);
      setMemberStats(members);
      setLastUpdate(new Date());
      setDebugInfo(
        `✅ ${t("dashboard.loadSuccess")}: ${
          financial.totalTransactions
        } ${t("dashboard.items")}, ${t("dashboard.profit")}: ${t("dashboard.currencySymbol")}${financial.netProfit.toLocaleString()}`
      );
    } catch (error: any) {
      logger.error("❌ [Dashboard] Failed to load data:", error);
      setApiError(error.message || t("dashboard.loadError"));
      setDebugInfo(`❌ ${t("dashboard.error")}: ${error.message}`);
      // Set empty stats on error so UI shows 0 instead of loading forever
      setFinancialStats({
        totalDepositAmount: 0,
        totalDepositRegularAmount: 0,
        totalDepositBonusAmount: 0,
        totalWithdrawAmount: 0,
        totalWithdrawRegularAmount: 0,
        totalWithdrawBonusAmount: 0,
        totalReturnToMemberAmount: 0,
        netProfit: 0,
        adminDepositAmount: 0,
        autoDepositAmount: 0,
        adminWithdrawAmount: 0,
        autoWithdrawAmount: 0,
        totalTransactions: 0,
        depositTransactions: 0,
        withdrawTransactions: 0,
        pendingTransactions: 0,
        approvedTransactions: 0,
      });
      setMemberStats({
        totalMembers: 0,
        totalNewMembers: 0,
        newMembersWithDeposit: 0,
        activeMembers: 0,
      });
    } finally {
      logger.debug("🔄 [Dashboard] Finally block - setting isLoading to false");
      setIsLoading(false);
      setIsRefreshing(false);
      logger.debug("✅ [Dashboard] isLoading set to false");
    }
  };

  useEffect(() => {
    loadData();
  }, [sessionToken, dateFilter]);

  // Format currency with validation
  const formatCurrency = (value: number) => {
    // Validate value - ensure it's a valid number
    const validValue = isNaN(value) || !isFinite(value) ? 0 : value;
    return `${t("dashboard.currencySymbol")}${validValue.toLocaleString(undefined, { minimumFractionDigits: 0 })}`;
  };

  // Get color class based on value (positive = green, negative = red, zero = gray)
  const getValueColor = (value: number) => {
    if (value > 0) return "text-emerald-600 dark:text-emerald-400";
    if (value < 0) return "text-rose-600 dark:text-rose-400";
    return "text-muted-foreground";
  };

  // Get icon based on value
  const getValueIcon = (value: number) => {
    if (value > 0) return TrendingUp;
    if (value < 0) return TrendingDown;
    return Activity;
  };

  // Get icon color class based on value
  const getIconColor = (value: number) => {
    if (value > 0) return "text-emerald-500";
    if (value < 0) return "text-rose-500";
    return "text-muted-foreground";
  };

  // Get background color class based on value
  const getBgColor = (value: number) => {
    if (value > 0) return "bg-emerald-500/10";
    if (value < 0) return "bg-rose-500/10";
    return "bg-muted";
  };

  // Calculate conversion rate with validation
  const conversionRate = useMemo(() => {
    if (!memberStats || memberStats.totalNewMembers === 0) return "0.0";
    const rate =
      (memberStats.newMembersWithDeposit / memberStats.totalNewMembers) * 100;
    // Validate calculation result
    const validRate = isNaN(rate) || !isFinite(rate) ? 0 : rate;
    return validRate.toFixed(1);
  }, [memberStats]);

  // Debug: Log render state (only in development)
  logger.debug(
    "🎨 [Dashboard] Rendering, isLoading:",
    isLoading,
    "hasStats:",
    !!financialStats,
    "hasMembers:",
    !!memberStats
  );

  return (
    <div className="space-y-6 overflow-x-hidden max-w-full">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between overflow-x-hidden">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            📈 {t("dashboard.title")}
          </h1>
          <p className="text-muted-foreground">{t("dashboard.subtitle")}</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Date Filter */}
          <Select
            value={dateFilter}
            onValueChange={(v) => setDateFilter(v as DateFilterType)}
          >
            <SelectTrigger className="w-[140px]">
              <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">
                {t("dashboard.dateFilter.today")}
              </SelectItem>
              <SelectItem value="yesterday">
                {t("dashboard.dateFilter.yesterday")}
              </SelectItem>
              <SelectItem value="7days">
                {t("dashboard.dateFilter.last7days")}
              </SelectItem>
              <SelectItem value="30days">
                {t("dashboard.dateFilter.last30days")}
              </SelectItem>
            </SelectContent>
          </Select>

          {/* System Status */}
          <Badge
            variant="secondary"
            className="gap-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-0 hidden sm:flex"
          >
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            {t("dashboard.systemOnline")}
          </Badge>

          {/* Refresh */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadData(false)}
            disabled={isRefreshing || isLoading}
            className="gap-2"
          >
            <RefreshCcw
              className={cn(
                "h-4 w-4",
                (isRefreshing || isLoading) && "animate-spin"
              )}
            />
            <span className="hidden sm:inline">
              {isRefreshing
                ? t("dashboard.refreshing") || "Refreshing..."
                : lastUpdate.toLocaleTimeString(undefined, {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
            </span>
          </Button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          Section 1: กำไรสุทธิ + ภาพรวมการเงิน
          ═══════════════════════════════════════════════════════════════ */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 overflow-x-hidden">
        {isLoading ? (
          <>
            <StatCardSkeleton size="large" />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            {/* กำไรสุทธิ - Hero Card */}
            {(() => {
              const netProfit = financialStats?.netProfit || 0;
              const ValueIcon = getValueIcon(netProfit);
              const cardBorderColor = netProfit >= 0 ? "border-emerald-500/30" : "border-rose-500/30";
              const cardBgGradient = netProfit >= 0 
                ? "bg-gradient-to-br from-emerald-500/10 via-transparent to-teal-500/5" 
                : "bg-gradient-to-br from-rose-500/10 via-transparent to-red-500/5";
              
              return (
                <Card className={cn("md:row-span-2", cardBgGradient, cardBorderColor)}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                          <Wallet className="h-4 w-4" />
                          {t("dashboard.netProfit")}
                        </p>
                        <p className={cn("text-3xl font-bold mt-2", getValueColor(netProfit))}>
                          {formatCurrency(netProfit)}
                        </p>
                        <p className="text-xs text-muted-foreground/70 mt-3">
                          {t("dashboard.netProfitFormula")}
                        </p>
                      </div>
                      <div className={cn("p-3 rounded-xl", getBgColor(netProfit))}>
                        <ValueIcon className={cn("h-6 w-6", getIconColor(netProfit))} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* ฝากเงินสด */}
            <StatCard
              title={t("dashboard.cashDeposit")}
              value={formatCurrency(
                financialStats?.totalDepositRegularAmount || 0
              )}
              icon={ArrowDownToLine}
              iconColor="text-emerald-600 dark:text-emerald-400"
              iconBg="bg-emerald-500/10"
            />

            {/* ถอนเงินสด */}
            <StatCard
              title={t("dashboard.cashWithdraw")}
              value={formatCurrency(
                financialStats?.totalWithdrawRegularAmount || 0
              )}
              icon={ArrowUpFromLine}
              iconColor="text-rose-600 dark:text-rose-400"
              iconBg="bg-rose-500/10"
            />

            {/* กระแสเงินสด */}
            {(() => {
              const cashFlow = (financialStats?.totalDepositRegularAmount || 0) -
                (financialStats?.totalWithdrawRegularAmount || 0);
              const CashFlowIcon = getValueIcon(cashFlow);
              
              return (
                <StatCard
                  title={t("dashboard.netCashFlow")}
                  value={formatCurrency(cashFlow)}
                  valueColor={getValueColor(cashFlow)}
                  icon={CashFlowIcon}
                  iconColor={getIconColor(cashFlow)}
                  iconBg={getBgColor(cashFlow)}
                  className={cashFlow < 0 ? "border-rose-500/20" : undefined}
                />
              );
            })()}
          </>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          Section 2: ค่าใช้จ่าย
          ═══════════════════════════════════════════════════════════════ */}
      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Gift className="h-5 w-5 text-amber-500" />
          {t("dashboard.expenses")}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading ? (
            <>
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
            </>
          ) : (
            <>
              <StatCard
                title={t("dashboard.bonusDeposit")}
                value={formatCurrency(
                  financialStats?.totalDepositBonusAmount || 0
                )}
                icon={Gift}
                iconColor="text-amber-600 dark:text-amber-400"
                iconBg="bg-amber-500/10"
              />
              <StatCard
                title={t("dashboard.lossback")}
                value={formatCurrency(
                  financialStats?.totalReturnToMemberAmount || 0
                )}
                icon={RotateCcw}
                iconColor="text-blue-600 dark:text-blue-400"
                iconBg="bg-blue-500/10"
              />
              <StatCard
                title={t("dashboard.depositCount")}
                value={financialStats?.depositTransactions || 0}
                subtitle={`${t("dashboard.pendingCount")}: ${
                  financialStats?.pendingTransactions || 0
                }`}
                icon={ArrowDownToLine}
                iconColor="text-emerald-600 dark:text-emerald-400"
                iconBg="bg-emerald-500/10"
              />
              <StatCard
                title={t("dashboard.withdrawCount")}
                value={financialStats?.withdrawTransactions || 0}
                icon={ArrowUpFromLine}
                iconColor="text-rose-600 dark:text-rose-400"
                iconBg="bg-rose-500/10"
              />
            </>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          Section 3: สถิติสมาชิก
          ═══════════════════════════════════════════════════════════════ */}
      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-500" />
          {t("dashboard.memberStats")}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading ? (
            <>
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
            </>
          ) : (
            <>
              <StatCard
                title={t("dashboard.totalMembers")}
                value={memberStats?.totalMembers || 0}
                icon={Users}
                iconColor="text-slate-600 dark:text-slate-400"
                iconBg="bg-slate-500/10"
              />
              <StatCard
                title={t("dashboard.newMembers")}
                value={memberStats?.totalNewMembers || 0}
                icon={UserPlus}
                iconColor="text-blue-600 dark:text-blue-400"
                iconBg="bg-blue-500/10"
              />
              <StatCard
                title={t("dashboard.newMembersDeposit")}
                value={memberStats?.newMembersWithDeposit || 0}
                subtitle={`${t(
                  "dashboard.conversionRate"
                )}: ${conversionRate}%`}
                icon={UserCheck}
                iconColor="text-emerald-600 dark:text-emerald-400"
                iconBg="bg-emerald-500/10"
              />
              <StatCard
                title={t("dashboard.activeMembers")}
                value={memberStats?.activeMembers || 0}
                subtitle={t("dashboard.fromGameplay")}
                icon={Activity}
                iconColor="text-cyan-600 dark:text-cyan-400"
                iconBg="bg-cyan-500/10"
              />
            </>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          Section 4: แยกตามวิธี (แอดมิน/อัตโนมัติ)
          ═══════════════════════════════════════════════════════════════ */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* ฝากแยกตามวิธี */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <ArrowDownToLine className="h-5 w-5 text-emerald-500" />
              {t("dashboard.depositByMethod")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              <>
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </>
            ) : (
              <>
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-indigo-500/10">
                      <User className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {t("dashboard.adminDeposit")}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t("dashboard.manualDeposit")}
                      </p>
                    </div>
                  </div>
                  <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                    {formatCurrency(financialStats?.adminDepositAmount || 0)}
                  </p>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-teal-500/10">
                      <Zap className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {t("dashboard.autoDeposit")}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t("dashboard.autoDeposit")}
                      </p>
                    </div>
                  </div>
                  <p className="text-xl font-bold text-teal-600 dark:text-teal-400">
                    {formatCurrency(financialStats?.autoDepositAmount || 0)}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* ถอนแยกตามวิธี */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <ArrowUpFromLine className="h-5 w-5 text-rose-500" />
              {t("dashboard.withdrawByMethod")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              <>
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </>
            ) : (
              <>
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-pink-500/10">
                      <User className="h-5 w-5 text-pink-600 dark:text-pink-400" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {t("dashboard.adminWithdraw")}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t("dashboard.manualWithdraw")}
                      </p>
                    </div>
                  </div>
                  <p className="text-xl font-bold text-pink-600 dark:text-pink-400">
                    {formatCurrency(financialStats?.adminWithdrawAmount || 0)}
                  </p>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/10">
                      <Zap className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {t("dashboard.autoWithdraw")}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t("dashboard.autoWithdraw")}
                      </p>
                    </div>
                  </div>
                  <p className="text-xl font-bold text-amber-600 dark:text-amber-400">
                    {formatCurrency(financialStats?.autoWithdrawAmount || 0)}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Links */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* ✅ ซ่อน quick links สำหรับ "รอดำเนินการ" และ "ประวัติทั้งหมด" ตามที่ผู้ใช้ขอ */}
        {/* <a 
          href="/transactions/pending"
          className="flex items-center h-auto py-4 px-4 rounded-lg border bg-background hover:bg-accent transition-colors"
        >
          <Clock className="h-5 w-5 mr-3 text-amber-500 flex-shrink-0" />
          <div className="text-left flex-1 min-w-0">
            <p className="font-medium">{t('dashboard.pendingTransactions')}</p>
            <p className="text-sm text-muted-foreground">
              {financialStats?.pendingTransactions || 0} {t('dashboard.items')}
            </p>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
        </a> */}

        <a
          href="/members"
          className="flex items-center h-auto py-4 px-4 rounded-lg border bg-background hover:bg-accent transition-colors"
        >
          <Users className="h-5 w-5 mr-3 text-blue-500 flex-shrink-0" />
          <div className="text-left flex-1 min-w-0">
            <p className="font-medium">{t("dashboard.manageMembers")}</p>
            <p className="text-sm text-muted-foreground">
              {memberStats?.totalMembers?.toLocaleString() || 0}{" "}
              {t("dashboard.people")}
            </p>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
        </a>

        {/* ✅ ซ่อน quick link สำหรับ "ประวัติทั้งหมด" */}
        {/* <a 
          href="/transactions/history"
          className="flex items-center h-auto py-4 px-4 rounded-lg border bg-background hover:bg-accent transition-colors"
        >
          <Activity className="h-5 w-5 mr-3 text-emerald-500 flex-shrink-0" />
          <div className="text-left flex-1 min-w-0">
            <p className="font-medium">{t('dashboard.transactionHistory')}</p>
            <p className="text-sm text-muted-foreground">{t('dashboard.viewAll')}</p>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
        </a> */}

        <a
          href="/audit"
          className="flex items-center h-auto py-4 px-4 rounded-lg border bg-background hover:bg-accent transition-colors"
        >
          <Building2 className="h-5 w-5 mr-3 text-violet-500 flex-shrink-0" />
          <div className="text-left flex-1 min-w-0">
            <p className="font-medium">{t("dashboard.auditLog")}</p>
            <p className="text-sm text-muted-foreground">
              {t("dashboard.usageHistory")}
            </p>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
        </a>
      </div>

      {/* Debug Info - Only show in development */}
      {import.meta.env.DEV && (debugInfo || apiError) && (
        <div
          className={`p-4 rounded-lg border ${
            apiError
              ? "bg-red-500/10 border-red-500/30"
              : "bg-blue-500/10 border-blue-500/30"
          }`}
        >
          <p
            className={`text-sm ${
              apiError
                ? "text-red-600 dark:text-red-400"
                : "text-blue-600 dark:text-blue-400"
            }`}
          >
            {apiError || debugInfo}
          </p>
        </div>
      )}

      {/* Error Message with Retry Button - Show in all environments */}
      {apiError && !import.meta.env.DEV && (
        <div className="p-4 rounded-lg border bg-red-500/10 border-red-500/30">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                {t("dashboard.error") || "Error"}
              </p>
              <p className="text-xs text-red-500/80 mt-1">{apiError}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadData()}
              className="gap-2"
            >
              <RefreshCcw className="h-4 w-4" />
              {t("dashboard.retry") || "Retry"}
            </Button>
          </div>
        </div>
      )}

      {/* Footer Note */}
      <div className="text-center py-4">
        <p className="text-sm text-muted-foreground">
          💡 {t("dashboard.footer")}
        </p>
      </div>
    </div>
  );
}
